import React from 'react';

interface NameEmailAvatarProps {
  name?: string;
  email?: string;
  avatar: string | undefined; // URL for the avatar image or empty for initials
  avatarWidth?: string;
  avatarHeight?: string;
  nameFontSize?: string;
  emailFontSize?: string;
  centerVertically?: boolean;
  applicantStatus?: string;
}

export const NameEmailAvatar: React.FC<NameEmailAvatarProps> = ({
  name = '',
  email = '',
  avatar = '',
  avatarWidth = '36px',
  avatarHeight = '36px',
  nameFontSize = '14px',
  emailFontSize = '11px',
  centerVertically = false,
  applicantStatus,
}) => {
  const getInitials = (name: string) => {
    const nameParts = name.split(' ');
    const firstNameInitial = nameParts[0]?.charAt(0).toUpperCase() || '';
    const lastNameInitial =
      nameParts.length > 1
        ? nameParts[nameParts.length - 1].charAt(0).toUpperCase()
        : '';
    return firstNameInitial + lastNameInitial;
  };

  // Elegant light theme color palette
  const avatarColors = [
    { bg: 'bg-blue-100', text: 'text-blue-800', border: 'border-blue-200' },
    {
      bg: 'bg-purple-100',
      text: 'text-purple-800',
      border: 'border-purple-200',
    },
    { bg: 'bg-green-100', text: 'text-green-800', border: 'border-green-200' },
    { bg: 'bg-pink-100', text: 'text-pink-800', border: 'border-pink-200' },
    {
      bg: 'bg-indigo-100',
      text: 'text-indigo-800',
      border: 'border-indigo-200',
    },
    { bg: 'bg-teal-100', text: 'text-teal-800', border: 'border-teal-200' },
    {
      bg: 'bg-orange-100',
      text: 'text-orange-800',
      border: 'border-orange-200',
    },
    { bg: 'bg-cyan-100', text: 'text-cyan-800', border: 'border-cyan-200' },
    {
      bg: 'bg-emerald-100',
      text: 'text-emerald-800',
      border: 'border-emerald-200',
    },
    {
      bg: 'bg-violet-100',
      text: 'text-violet-800',
      border: 'border-violet-200',
    },
    { bg: 'bg-rose-100', text: 'text-rose-800', border: 'border-rose-200' },
    { bg: 'bg-amber-100', text: 'text-amber-800', border: 'border-amber-200' },
  ];

  const getAvatarColor = (name: string) => {
    // Use the first letter of the name for consistent color selection
    const firstLetter = name.trim().charAt(0).toUpperCase();
    if (!firstLetter) return avatarColors[0]; // Default to first color if no name

    // Map letters to colors (A=0, B=1, etc., cycling through available colors)
    const charCode = firstLetter.charCodeAt(0);
    const index = (charCode - 65) % avatarColors.length; // 65 is 'A' in ASCII
    return avatarColors[Math.max(0, index)]; // Ensure positive index
  };

  const colorScheme = getAvatarColor(name);

  return (
    <div
        className={`flex gap-3 ${
          centerVertically ? 'items-center flex-col text-center' : ''
        }`}
    >
      { /* Avatar */ }
      { avatar &&
      avatar.trim() !== '' &&
      !avatar.includes('undefined') &&
      !avatar.includes('null') ? (
        <img
              src={avatar}
              alt={name}
              className='rounded-full object-cover shadow-sm shrink-0'
              style={{ width: avatarWidth, height: avatarHeight, minWidth: avatarWidth, minHeight: avatarHeight }}
              onError={(e) => {
              // If image fails to load, replace with initials
              e.currentTarget.style.display = 'none';
              const sibling = e.currentTarget.nextElementSibling;
              if (sibling instanceof HTMLElement) {
                sibling.style.display = 'flex';
              }
            }}
          />
        ) : null }
      <div
          className={`flex items-center justify-center rounded-full font-bold shadow-sm border-2 transition-all duration-200 hover:shadow-md shrink-0 ${colorScheme.bg} ${colorScheme.text} ${colorScheme.border}`}
          style={{
            width: avatarWidth,
            height: avatarHeight,
            minWidth: avatarWidth,
            minHeight: avatarHeight,
            fontSize: `calc(${avatarWidth} / 2.5)`,
            display:
            avatar &&
            avatar.trim() !== '' &&
            !avatar.includes('undefined') &&
            !avatar.includes('null')
              ? 'none'
              : 'flex',
          }}
      >
        { getInitials(name) }
      </div>

      { /* Name & Email */ }
      <div
          className={`${
            centerVertically
              ? 'flex flex-col text-ccenter justify-center'
              : 'text-start '
          }`}
      >
        <p
            className='font-medium text-sm text-primary-text'
            style={{ fontSize: nameFontSize }}
        >
          { name }
        </p>
        <p
            className='text-primary-textGray text-sm'
            style={{ fontSize: emailFontSize }}
        >
          { email }
        </p>
        { applicantStatus && (
          <div className=''>
            <span
                className={`inline-flex items-center  rounded-full text-[10px] font-medium ${
                  applicantStatus === 'Outstanding'
                    ? ' text-blue-800'
                    : applicantStatus === 'Auto-Assessment'
                      ? ' text-purple-800'
                      : applicantStatus === 'Shortlisted'
                        ? ' text-green-800'
                        : applicantStatus === 'Unsuccessful'
                          ? ' text-red-800'
                          : applicantStatus === 'Suitable'
                            ? ' text-teal-800'
                            : applicantStatus === 'New'
                              ? ' text-yellow-800'
                              : applicantStatus === 'Completed'
                                ? ' text-emerald-800'
                                : ' text-gray-800'
                }`}
            >
              <span
                  className={`w-2 h-2 mr-1.5 rounded-full ${
                    applicantStatus === 'Outstanding'
                      ? 'bg-blue-500'
                      : applicantStatus === 'Auto-Assessment'
                        ? 'bg-purple-500'
                        : applicantStatus === 'Shortlisted'
                          ? 'bg-green-500'
                          : applicantStatus === 'Unsuccessful'
                            ? 'bg-red-500'
                            : applicantStatus === 'Suitable'
                              ? 'bg-teal-500'
                              : applicantStatus === 'New'
                                ? 'bg-yellow-500'
                                : applicantStatus === 'Completed'
                                  ? 'bg-emerald-500'
                                  : 'bg-gray-500'
                  }`}
              ></span>
              { applicantStatus }
            </span>
          </div>
        ) }
      </div>
    </div>
  );
};
